import React, { useState } from 'react';
import { Row } from '@src/components/Utils';
import { FormError } from '@src/components/Form/FormError';
import { stylesheet } from 'typestyle';
import { api } from '@src/services/api';
import { LocalizedStrings } from '@src/utils/localizedStrings';
import { Loader } from '@src/components/Loader';
import logo from '../../assets/img/site/logo_alone_white.png';
import { LoginContainer } from './container/LoginContainer';
import { OnboardEmailSent } from './OnboardEmailSent';
import { LoginTextInput } from './TextInput';
import { LoginFrameContainer } from './container/FrameContainer';
import { LoginSigninLabel } from './SigninLabel';
import { LoginSubmitButton } from './LoginSubmitButton';
import Captcha from './Captcha';
import { isEmailValid } from './utils';

export default function Onboard(): JSX.Element {
  const [email, setEmail] = useState<string>("");
  const [error, setError] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<'onpage' | 'emailsent'>('onpage');
  const [loading, setLoading] = useState<boolean>(false);
  const [captchaToken, setCaptchaToken] = useState<string | undefined>(undefined);

  function handleEmailChange(event: { target: { value: string } }) {
    setEmail(event.target.value);
  }

  function handleCaptchaTokenChange(token: string) {
    setCaptchaToken(token);
  }

  function sendOnboardEmail() {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError('Missing email');
      return;
    }
    if (!isEmailValid(trimmedEmail)) {
      setError('Invalid email');
      return;
    }
    if (!captchaToken) {
      setError("Invalid Captcha");
      return;
    }
    
    setLoading(true);
    setError(undefined);
    
    api.resetPassword(trimmedEmail, captchaToken).then((r) => {
      if ((r as unknown as string) != 'ok') {      
        setError(`Something went wrong: ${r}`);
        setLoading(false);
        return;
      }
      setLoading(false);
      setStatus('emailsent');
    }).catch((e) => {
      setError(e.message);
      setLoading(false);
    });
  }

  return (
    <LoginContainer>
      <div className="logo">
        <img src={logo} />
      </div>
      <LoginFrameContainer>
        {status === 'onpage' && !loading
          ? (
            <>
              <LoginSigninLabel title={lang.title} />
              <Row>
                <div id="script_cf" ></div>
                <div className="col-sm-10 offset-sm-1">
                  <span style={{
                    fontSize: 12, fontWeight: 'bold', color: '#CCC', lineHeight: '15px', display: 'block',
                  }}
                  >
                    {lang.description}
                  </span>
                </div>
              </Row>
              <Row>
                <div className="col-sm-10 offset-sm-1">
                  <LoginTextInput
                    label="email"
                    type="email"
                    fullWidth
                    value={email}
                    onChange={handleEmailChange}
                  />
                </div>
              </Row>
              <Row>
                <Captcha
                  onTokenChange={handleCaptchaTokenChange}
                />
              </Row>
              <Row className={sheet.submitRow}>
                <LoginSubmitButton
                  text={lang.submit.toUpperCase()}
                  onClick={sendOnboardEmail}
                  disabled={loading}
                />
              </Row>
              {error
                ? (
                  <FormError
                    error={error}
                    level="error"
                  />
                )
                : null}
            </>
          ) : null}
        {loading && (
          <div className={sheet.loaderContainer}>
            <Loader size={30} />
          </div>
        )}
        {status === 'emailsent'
          ? (<OnboardEmailSent email={email} />) : null}
      </LoginFrameContainer>
    </LoginContainer>
  );
}

const lang = LocalizedStrings({
  en: {
    title: 'First Login',
    description: 'Enter your email address and we\'ll send you instructions to set up your account',
    submit: 'Submit',
  },
});

const sheet = stylesheet({
  submitRow: {
    margin: '7px 0',
  },
  loaderContainer: {
    margin: 15,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
});
